import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  useColorModeValue,
  ModalBody,
  ModalFooter,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  AspectRatio
} from "@chakra-ui/react";
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaCheck, 
  FaQuestionCircle, 
  FaPalette, 
  FaDatabase, 
  FaEye, 
  FaPaperPlane,
  FaArrowRight,
  FaBook,
  FaVideo
} from "react-icons/fa";
import { useRequestFormModal } from "./RequestFormModal";

const GuideModal = ({ isOpen, onClose, accentColor, settings }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [guideMode, setGuideMode] = useState("text"); // "text" or "video"
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.700", "gray.100");
  const { onOpen: openRequestForm, RequestFormModalComponent } = useRequestFormModal(settings || {});

  const handlePreview = () => {
    onClose();
    // Store current settings in localStorage for preview
    const currentSettings = localStorage.getItem("settings");
    if (currentSettings) {
      localStorage.setItem("previewSettings", currentSettings);
      window.open("/preview", "_blank", "noopener");
    }
  };
  
  const handleRequestChatbot = () => {
    onClose();
    // Open the request form modal
    if (typeof openRequestForm === 'function') {
      openRequestForm();
    }
  };

  // Updated guide steps with YouTube video URLs
  const guideSteps = [
    {
      title: "Step 1: Add Website Data",
      description: "Extract content from your website to provide your chatbot with information about your products, services, and company.",
      icon: FaDatabase,
      color: "purple.400",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80",
      tips: [
        { label: "Tip 1", text: "Go to the 'Data' tab to extract content from your website" },
        { label: "Tip 2", text: "Enter your website URL and click 'Extract Website Data'" },
        { label: "Tip 3", text: "The extracted content will automatically be saved to your widget" }
      ],
      action: null,
      videoUrl: "https://www.youtube.com/embed/voZXA6yjv-U" // YouTube embed URL
    },
    {
      title: "Step 2: Design & Preview Widget",
      description: "Customize your chatbot's appearance and preview how it will look on your website.",
      icon: FaPalette,
      color: "blue.400",
      image: "https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?auto=format&fit=crop&q=80",
      tips: [
        { label: "Appearance", text: "Choose colors, avatar and font size in the 'Appearance' tab" },
        { label: "Content", text: "Customize welcome messages and bot name in the 'Content' tab" },
        { label: "Size", text: "Adjust widget dimensions in the 'Size' tab to fit your website" }
      ],
      action: {
        label: "Preview Widget",
        handler: handlePreview,
        icon: FaEye
      },
      videoUrl: "https://www.youtube.com/embed/voZXA6yjv-U" // YouTube embed URL
    },
    {
      title: "Step 3: Request Your Widget",
      description: "Once you've customized your widget, submit your request and we'll deliver it to your email within a few minutes.",
      icon: FaPaperPlane,
      color: "teal.400",
      image: "https://images.unsplash.com/photo-1555421689-491a97ff2040?auto=format&fit=crop&w=1050&q=80",
      tips: [
        { label: "Step 1", text: "Click 'Request Widget' after finalizing your design" },
        { label: "Step 2", text: "Enter your email address and website details" },
        { label: "Step 3", text: "Our team will deliver your custom widget within a few minutes." }
      ],
      action: {
        label: "Request Widget Now",
        handler: handleRequestChatbot,
        icon: FaPaperPlane
      },
      videoUrl: "https://www.youtube.com/embed/voZXA6yjv-U" // YouTube embed URL
    }
  ];

  const nextStep = () => {
    if (currentStep < guideSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        size="xl" 
        motionPreset="slideInBottom"
        isCentered
      >
        <ModalOverlay backdropFilter="blur(3px)" />
        <ModalContent 
          borderRadius="xl" 
          overflow="hidden"
          maxW="800px"
          bg={bgColor}
        >
          <Box position="relative">
            <Box 
              h="220px" 
              bgImage={`url(${guideSteps[currentStep]?.image || ""})`}
              bgSize="cover"
              bgPosition="center"
              position="relative"
            >
              <Box 
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="rgba(0, 0, 0, 0.5)"
                backdropFilter="blur(2px)"
              />
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                p={6}
                color="white"
              >
                <Icon 
                  as={guideSteps[currentStep]?.icon} 
                  w={16} 
                  h={16} 
                  mb={4} 
                  color={guideSteps[currentStep]?.color}
                  filter="drop-shadow(0px 2px 5px rgba(0,0,0,0.3))"
                />
                <Heading 
                  size="xl" 
                  textAlign="center" 
                  fontWeight="bold"
                  textShadow="0px 2px 4px rgba(0,0,0,0.4)"
                >
                  {guideSteps[currentStep]?.title}
                </Heading>
              </Box>
            </Box>
            
            <ModalCloseButton color="white" zIndex={2} />
          </Box>

          <ModalBody py={8} px={8}>
            {/* Guide mode toggle */}
          <Tabs 
            isFitted 
            variant="soft-rounded" 
            colorScheme={guideSteps[currentStep]?.color?.split('.')[0]}
            mb={6}
            onChange={(index) => setGuideMode(index === 0 ? "text" : "video")}
            defaultIndex={1} // Start with video guide selected
          >
              <TabList>
                <Tab 
                  _selected={{ 
                    color: `${guideSteps[currentStep]?.color?.split('.')[0]}.600`, 
                    bg: `${guideSteps[currentStep]?.color?.split('.')[0]}.100` 
                  }}
                  fontWeight="medium"
                >
                  <Icon as={FaBook} mr={2} /> Text Guide
                </Tab>
                <Tab 
                  _selected={{ 
                    color: `${guideSteps[currentStep]?.color?.split('.')[0]}.600`, 
                    bg: `${guideSteps[currentStep]?.color?.split('.')[0]}.100` 
                  }}
                  fontWeight="medium"
                >
                  <Icon as={FaVideo} mr={2} /> Video Guide
                </Tab>
              </TabList>
              
              <TabPanels mt={4}>
                {/* Text Guide Panel */}
                <TabPanel p={0}>
                  <VStack spacing={8} align="stretch">
                    <Text fontSize="lg" lineHeight="tall" color={textColor}>
                      {guideSteps[currentStep]?.description}
                    </Text>
                    
                    {/* Step-specific tips section */}
                    <Box 
                      borderRadius="lg" 
                      p={5} 
                      bg={`${guideSteps[currentStep]?.color?.split('.')[0]}.50`}
                      borderLeft="4px solid"
                      borderLeftColor={guideSteps[currentStep]?.color}
                    >
                      <VStack align="stretch" spacing={4}>
                        <HStack>
                          <Icon as={FaQuestionCircle} color={guideSteps[currentStep]?.color} />
                          <Text fontWeight="semibold" color={`${guideSteps[currentStep]?.color?.split('.')[0]}.700`}>
                            Tips:
                          </Text>
                        </HStack>
                        
                        {guideSteps[currentStep]?.tips?.map((tip, index) => (
                          <HStack 
                            key={index} 
                            pl={2}
                            alignItems="flex-start"
                            spacing={3}
                          >
                            <Badge 
                              colorScheme={guideSteps[currentStep]?.color?.split('.')[0]} 
                              px={2} py={1}
                              borderRadius="md"
                              fontSize="sm"
                            >
                              {tip.label}
                            </Badge>
                            <Text color={textColor}>{tip.text}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>
                
                {/* Video Guide Panel - Updated to use YouTube iframe */}
                <TabPanel p={0}>
                  {guideSteps[currentStep]?.videoUrl ? (
                    <Box borderRadius="lg" overflow="hidden" boxShadow="md">
                      <AspectRatio ratio={16 / 9}>
                        <iframe
                          src={guideSteps[currentStep].videoUrl}
                          title="Widget Creator Guide"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ 
                            width: '100%', 
                            height: '100%',
                            borderRadius: 'lg',
                          }}
                        />
                      </AspectRatio>
                    </Box>
                  ) : (
                    <Box 
                      p={6} 
                      textAlign="center" 
                      borderRadius="lg" 
                      bg="gray.50"
                    >
                      <Text color="gray.500">Video guide not available for this step.</Text>
                    </Box>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>

            {/* Action button if available */}
            {guideSteps[currentStep]?.action && (
              <Button
                leftIcon={<Icon as={guideSteps[currentStep].action.icon} />}
                rightIcon={<FaArrowRight />}
                onClick={guideSteps[currentStep].action.handler}
                colorScheme={guideSteps[currentStep].color?.split('.')[0]}
                size="md"
                width="full"
                height="48px"
                mt={6}
              >
                {guideSteps[currentStep].action.label}
              </Button>
            )}
            
            {/* Progress indicators */}
            <HStack justify="center" spacing={3} pt={6}>
              {guideSteps.map((_, index) => (
                <Box
                  key={index}
                  w={index === currentStep ? "10px" : "8px"}
                  h={index === currentStep ? "10px" : "8px"}
                  borderRadius="full"
                  bg={index === currentStep ? guideSteps[currentStep]?.color : "gray.300"}
                  transform={index === currentStep ? "scale(1.2)" : "scale(1)"}
                  transition="all 0.2s"
                  cursor="pointer"
                  onClick={() => setCurrentStep(index)}
                />
              ))}
            </HStack>
          </ModalBody>

          <ModalFooter 
            borderTop="1px solid" 
            borderColor="gray.100" 
            p={5}
            bg={useColorModeValue("gray.50", "gray.700")}
          >
            <HStack spacing={4} width="100%" justify="space-between">
              <Button 
                leftIcon={<FaChevronLeft />}
                onClick={prevStep}
                isDisabled={currentStep === 0}
                variant="outline"
                size="md"
                borderRadius="md"
              >
                Previous
              </Button>
              
              <HStack>
                <Text color="gray.500" fontWeight="medium">
                  {currentStep + 1} of {guideSteps.length}
                </Text>
              </HStack>
              
              {currentStep < guideSteps.length - 1 ? (
                <Button 
                  rightIcon={<FaChevronRight />}
                  onClick={nextStep}
                  colorScheme={guideSteps[currentStep]?.color?.split('.')[0]}
                  size="md"
                  borderRadius="md"
                >
                  Next
                </Button>
              ) : (
                <Button 
                  rightIcon={<FaCheck />}
                  onClick={onClose}
                  colorScheme="teal"
                  size="md"
                  borderRadius="md"
                >
                  Got It
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {RequestFormModalComponent && <RequestFormModalComponent accentColor={accentColor} />}
    </>
  );
};

export default GuideModal;